import { GlassCard } from "@/components/ui/glass-card";
import { Button } from "@/components/ui/button";
import { Terminal, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import StatusBadge from "@/components/StatusBadge";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

interface Deployment {
  id: string;
  status: string;
  last_run: string | null;
  configurations?: {
    name: string;
  };
}

interface DeploymentHistoryProps {
  deployments?: Deployment[];
}

const DeploymentHistory = ({ deployments }: DeploymentHistoryProps) => {
  const isMobile = useIsMobile();

  return (
    <GlassCard variant="default" hover={false} className={cn(isMobile ? "p-3" : "p-6")}>
      <div className={cn(
        "mb-3 sm:mb-4",
        isMobile
          ? "flex flex-col gap-2"
          : "flex justify-between items-center"
      )}>
        <h2 className={cn(
          "font-semibold text-slate-900 dark:text-white",
          isMobile ? "text-base" : "text-xl"
        )}>
          Recent Deployments
        </h2>
        <Link to="/configurations" className={isMobile ? "self-start" : ""}>
          <Button
            variant="outline"
            size={isMobile ? "sm" : "default"}
            className="flex items-center gap-2"
          >
            View Configurations
            <ChevronRight className="h-4 w-4" />
          </Button>
        </Link>
      </div>
      <div className={cn(isMobile ? "space-y-2" : "space-y-4")}>
        {deployments?.map((deployment) => (
          <div
            key={deployment.id}
            className={cn(
              "flex items-center justify-between bg-slate-50 dark:bg-slate-900 rounded-lg",
              isMobile ? "p-3 gap-2" : "p-4"
            )}
          >
            <div className={cn("flex items-center min-w-0", isMobile ? "gap-2" : "gap-3")}>
              <Terminal className={cn("flex-shrink-0 text-info", isMobile ? "h-4 w-4" : "h-5 w-5")} />
              <div className="min-w-0">
                <p className={cn(
                  "font-medium text-slate-900 dark:text-white truncate",
                  isMobile ? "text-sm" : ""
                )}>
                  {deployment.configurations?.name || 'Unnamed Script'}
                </p>
                <p className={cn("text-slate-500 truncate", isMobile ? "text-xs" : "text-sm")}>
                  Last run: {deployment.last_run ? new Date(deployment.last_run).toLocaleString() : 'Never'}
                </p>
              </div>
            </div>
            <StatusBadge status={deployment.status} />
          </div>
        ))}
        {(!deployments || deployments.length === 0) && (
          <p className={cn("text-center text-slate-500", isMobile ? "text-sm py-2" : "")}>
            No deployments found
          </p>
        )}
      </div>
    </GlassCard>
  );
};

export default DeploymentHistory;
